/**
 * The connection supervisor: the single owner of connect/reconnect and
 * cloud-auth-failure state.
 *
 * This is the decision core of upstream T3 Code's Effect `EnvironmentSupervisor`
 * (`@t3tools/client-runtime/connection`, upstream pin e3e7cc3f), ported to a
 * sequential plain-TypeScript state machine so hosts without an Effect runtime
 * can adopt it. Upstream's supervisor owns the retry loop itself; this core
 * owns the DECISIONS and lets the host drive attempts:
 *
 * - phase transitions across available / offline / connecting / backoff /
 *   connected / blocked,
 * - the backoff ladder (3s, 4s, 8s, then 16s held, reset after 30s of stable
 *   connection) with an optional bounded retry count,
 * - a per-attempt credential epoch: the counter advances on every attempt, and
 *   hosts must re-resolve credentials (bearer token, WS ticket) for each new
 *   epoch instead of reusing the previous attempt's,
 * - the cloud auth-failure latch (idempotent expiry detection) with
 *   single-flight re-auth admission and the exempt-path redirect decision.
 *
 * Hosts report events (attempt started, connected, failed, 401 seen) and read
 * decisions back. Forge's web app adapters live in `apps/web/src/rpc`; the
 * Effect supervisor for the vendored runtime lives in
 * `packages/t3-client-runtime/src/connection/supervisor.ts`.
 */

/** Upstream's retry ladder, in milliseconds. The last rung repeats. */
export const UPSTREAM_RETRY_DELAYS_MS: ReadonlyArray<number> = [3_000, 4_000, 8_000, 16_000];

/** Connected for at least this long and the ladder restarts from the first rung. */
export const UPSTREAM_BACKOFF_RESET_AFTER_MS = 30_000;

export type ConnectionNetworkStatus = "online" | "offline";

export type ConnectionSupervisorPhase =
  | "available"
  | "offline"
  | "connecting"
  | "backoff"
  | "connected"
  | "blocked";

export type ConnectionFailureKind = "transient" | "blocked";

export interface ConnectionFailure {
  readonly kind: ConnectionFailureKind;
  readonly message: string;
  /**
   * Upstream's blocked/transient reason, when the host classifies one
   * (`"authentication"`, `"unsupported"`, ...). Presentation uses it to tell a
   * sign-in problem apart from a transient one.
   */
  readonly reason?: string;
}

export interface ConnectionSupervisorState {
  /** Whether anyone has asked for a connection. */
  readonly desired: boolean;
  readonly network: ConnectionNetworkStatus;
  readonly phase: ConnectionSupervisorPhase;
  /** 1-based number of the most recent attempt. 0 before the first one. */
  readonly attempt: number;
  /** Increments each time a connection is established. */
  readonly generation: number;
  readonly lastFailure: ConnectionFailure | null;
  /** Absolute clock time of the next retry, or null while not waiting on one. */
  readonly retryAtMs: number | null;
  /** True when a bounded ladder ran out. Upstream's default is unbounded. */
  readonly retriesExhausted: boolean;
  /**
   * Advances on every attempt. Credentials resolved for epoch N are stale once
   * N+1 begins: re-resolve per attempt, never reuse.
   */
  readonly credentialsEpoch: number;
}

export interface ConnectionSupervisorConfig {
  /** Retry delays in milliseconds. Defaults to upstream's ladder. */
  readonly retryDelaysMs?: ReadonlyArray<number>;
  /**
   * How many transient failures to retry before giving up (`retriesExhausted`).
   * `null`, the default and upstream's behavior, retries forever.
   */
  readonly maxRetries?: number | null;
  /** Stability window that resets the ladder. Defaults to upstream's 30s. */
  readonly backoffResetAfterMs?: number;
  readonly now?: () => number;
}

export interface ConnectionSupervisor {
  readonly getState: () => ConnectionSupervisorState;
  /** Ask for a connection. Starts the first attempt unless one is under way. */
  readonly connect: () => ConnectionSupervisorState;
  /** Drop the intent and return to available. Resets the ladder. */
  readonly disconnect: () => ConnectionSupervisorState;
  /** Abandon the current backoff and start the next attempt from rung one. */
  readonly retryNow: () => ConnectionSupervisorState;
  /** Report a network change. Offline pauses retries; online resumes them. */
  readonly setNetwork: (network: ConnectionNetworkStatus) => ConnectionSupervisorState;
  /** The host began a connection attempt. Advances the credential epoch. */
  readonly attemptStarted: () => ConnectionSupervisorState;
  /** The attempt established a connection. */
  readonly attemptConnected: () => ConnectionSupervisorState;
  /** The attempt failed (or a live connection dropped). */
  readonly attemptFailed: (failure: {
    readonly kind: ConnectionFailureKind;
    readonly message: string;
    readonly reason?: string;
  }) => ConnectionSupervisorState;
  readonly subscribe: (listener: (state: ConnectionSupervisorState) => void) => () => void;
}

export function createConnectionSupervisor(
  config: ConnectionSupervisorConfig = {},
): ConnectionSupervisor {
  const retryDelaysMs = config.retryDelaysMs ?? UPSTREAM_RETRY_DELAYS_MS;
  // An empty ladder would collapse the retry lookup to 0 and schedule the next
  // attempt with no backoff at all, so reject it up front.
  if (retryDelaysMs.length === 0) {
    throw new Error("retryDelaysMs must not be empty");
  }
  const maxRetries = config.maxRetries ?? null;
  const backoffResetAfterMs = config.backoffResetAfterMs ?? UPSTREAM_BACKOFF_RESET_AFTER_MS;
  const now = config.now ?? (() => Date.now());

  let state: ConnectionSupervisorState = {
    desired: false,
    network: "online",
    phase: "available",
    attempt: 0,
    generation: 0,
    lastFailure: null,
    retryAtMs: null,
    retriesExhausted: false,
    credentialsEpoch: 0,
  };
  const listeners = new Set<(state: ConnectionSupervisorState) => void>();
  // When the current (or most recent) connection was established. Null while
  // no connection is up. A failure after backoffResetAfterMs resets the ladder.
  let connectedAtMs: number | null = null;
  let failureCount = 0;

  const publish = (next: ConnectionSupervisorState): ConnectionSupervisorState => {
    state = next;
    for (const listener of listeners) listener(state);
    return state;
  };

  const startAttempt = (): ConnectionSupervisorState =>
    publish({
      ...state,
      phase: "connecting",
      attempt: failureCount + 1,
      retryAtMs: null,
      credentialsEpoch: state.credentialsEpoch + 1,
    });

  // Upstream: the Nth failure waits `retryDelaysMs[N - 1]`, holding the final
  // rung once the ladder is spent.
  const retryDelayMs = (): number =>
    retryDelaysMs[Math.min(Math.max(failureCount - 1, 0), retryDelaysMs.length - 1)] ?? 0;

  return {
    getState: () => state,

    connect: () => {
      if (state.desired) return state;
      // The connect signal only flips the intent and marks the want to
      // connect. The attempt itself (attempt number, credential epoch) begins
      // when the host reports attemptStarted.
      if (state.phase === "available") {
        return publish({ ...state, desired: true, phase: "connecting" });
      }
      return publish({ ...state, desired: true });
    },

    disconnect: () => {
      if (!state.desired && state.phase === "available") return state;
      failureCount = 0;
      connectedAtMs = null;
      return publish({
        ...state,
        desired: false,
        phase: "available",
        attempt: 0,
        lastFailure: null,
        retryAtMs: null,
        retriesExhausted: false,
      });
    },

    retryNow: () => {
      if (!state.desired && state.phase === "available") return state;
      // A fresh sequence, never a resumed one: the wait abandons the current
      // rung and the next attempt starts from the first.
      failureCount = 0;
      return publish({ ...state, retryAtMs: null, retriesExhausted: false });
    },

    setNetwork: (network) => {
      if (state.network === network) return state;
      if (network === "offline") {
        connectedAtMs = null;
        return publish({
          ...state,
          network,
          phase: state.desired ? "offline" : "available",
          retryAtMs: null,
        });
      }
      // Back online: an offline pause ends, but the attempt itself begins when
      // the host reports it, so no epoch change here.
      if (state.desired && state.phase === "offline") {
        return publish({ ...state, network, phase: "connecting" });
      }
      return publish({ ...state, network });
    },

    attemptStarted: () => startAttempt(),

    attemptConnected: () => {
      connectedAtMs = now();
      return publish({
        ...state,
        phase: "connected",
        generation: state.generation + 1,
        lastFailure: null,
        retryAtMs: null,
        retriesExhausted: false,
      });
    },

    attemptFailed: (failure) => {
      const stableForMs = connectedAtMs === null ? 0 : now() - connectedAtMs;
      connectedAtMs = null;
      if (stableForMs >= backoffResetAfterMs) {
        // The last connection held long enough to be called stable, so this
        // failure starts a fresh ladder instead of extending the old one.
        failureCount = 0;
      }

      const withFailure: ConnectionSupervisorState = { ...state, lastFailure: failure };

      if (failure.kind === "blocked") {
        // Terminal for now: no timer, no retry. An explicit retryNow or a
        // credentials change is what earns the next attempt.
        return publish({ ...withFailure, phase: "blocked", retryAtMs: null });
      }

      if (state.network === "offline") {
        // The outage swallows the failure: wait it out, do not escalate.
        return publish({ ...withFailure, phase: "offline", retryAtMs: null });
      }

      failureCount += 1;
      if (maxRetries !== null && failureCount > maxRetries) {
        return publish({
          ...withFailure,
          phase: "backoff",
          retryAtMs: null,
          retriesExhausted: true,
        });
      }
      return publish({
        ...withFailure,
        phase: "backoff",
        retryAtMs: now() + retryDelayMs(),
        retriesExhausted: false,
      });
    },

    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

// ── Cloud auth-failure recovery ──────────────────────────────────────────────

export type CloudAuthFailureSource = "http" | "ws-token" | "cmmd-runtimes";

export interface CloudAuthRecoveryState {
  readonly status: "ok" | "expired";
  readonly detectedAtMs: number | null;
  readonly source: CloudAuthFailureSource | null;
  /** A re-auth flight (login redirect, device-OAuth flow) is under way. */
  readonly reAuthInFlight: boolean;
}

export interface CloudAuthRecoveryConfig {
  readonly now?: () => number;
}

export interface CloudAuthRecovery {
  readonly getState: () => CloudAuthRecoveryState;
  /**
   * Report an auth failure (401 on the cloud session). Latching is idempotent:
   * once expired, later reports keep the first detection time and source, so
   * concurrent HTTP and WS-token 401s cannot thrash recovery UI.
   */
  readonly reportAuthFailure: (source: CloudAuthFailureSource) => CloudAuthRecoveryState;
  /** Reset to ok. Exempt paths and successful renews call this. */
  readonly clearAuthFailure: () => CloudAuthRecoveryState;
  /**
   * Ask to start one re-auth flight. True grants admission; false means
   * another flight is already running and the caller must not start a second
   * redirect. This is the single-flight guard that keeps a forced 401 from
   * looping.
   */
  readonly beginReAuth: () => boolean;
  /** End the in-flight re-auth admission. */
  readonly endReAuth: () => void;
  readonly subscribe: (listener: (state: CloudAuthRecoveryState) => void) => () => void;
}

export function createCloudAuthRecovery(config: CloudAuthRecoveryConfig = {}): CloudAuthRecovery {
  const now = config.now ?? (() => Date.now());
  let state: CloudAuthRecoveryState = {
    status: "ok",
    detectedAtMs: null,
    source: null,
    reAuthInFlight: false,
  };
  const listeners = new Set<(state: CloudAuthRecoveryState) => void>();

  const publish = (next: CloudAuthRecoveryState): CloudAuthRecoveryState => {
    state = next;
    for (const listener of listeners) listener(state);
    return state;
  };

  return {
    getState: () => state,

    reportAuthFailure: (source) => {
      if (state.status === "expired") return state;
      return publish({
        status: "expired",
        detectedAtMs: now(),
        source,
        reAuthInFlight: state.reAuthInFlight,
      });
    },

    clearAuthFailure: () =>
      publish({
        status: "ok",
        detectedAtMs: null,
        source: null,
        reAuthInFlight: state.reAuthInFlight,
      }),

    beginReAuth: () => {
      if (state.reAuthInFlight) return false;
      publish({ ...state, reAuthInFlight: true });
      return true;
    },

    endReAuth: () => {
      if (!state.reAuthInFlight) return;
      publish({ ...state, reAuthInFlight: false });
    },

    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

// ── Re-auth prompting decision ───────────────────────────────────────────────

export type CloudAuthRedirectDecision =
  | { readonly kind: "navigate-login" }
  | { readonly kind: "clear-failure" }
  | { readonly kind: "none" };

export interface CloudAuthRedirectInput {
  /** `recovery.getState().status`. */
  readonly failureStatus: CloudAuthRecoveryState["status"];
  /** Whether the current surface is a web surface that can reach /login. */
  readonly isWebSurface: boolean;
  /** Whether the current path is exempt (/login, /pair). */
  readonly isAuthExemptPath: boolean;
}

/**
 * Whether a detected cloud auth failure should navigate to login, should clear
 * itself because we are already on an exempt path (the redirect-loop break),
 * or should do nothing.
 */
export function resolveCloudAuthRedirectAction(
  input: CloudAuthRedirectInput,
): CloudAuthRedirectDecision {
  if (!input.isWebSurface) return { kind: "none" };
  if (input.failureStatus !== "expired") return { kind: "none" };
  if (input.isAuthExemptPath) return { kind: "clear-failure" };
  return { kind: "navigate-login" };
}
